import * as React from 'react'

import { observer } from 'mobx-react'

import { AvatarSize } from '~/client/src/shared/components/Avatar/Avatar'
import { Button } from '~/client/src/shared/components/Button/Button'
import { ICompanyMetadata } from '~/client/src/shared/components/CompanyListSelector'
import { AddIcon } from '~/client/src/shared/components/icons/AddIcon'
import { DeleteIcon } from '~/client/src/shared/components/icons/DeleteIcon'
import { InfoIcon } from '~/client/src/shared/components/icons/InfoIcon'
import { PopoverTagPicker } from '~/client/src/shared/components/PopoverTagPicker/PopoverTagPicker'
import { Tag } from '~/client/src/shared/components/Tag/Tag'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import {
  GroupingOption,
  ICurrentUserInfo,
  IFilterData,
  IUserMetadata,
  UserListSelector,
} from '~/client/src/shared/components/UserListSelector'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'
import OperationRule from '~/client/src/shared/models/OperationRule'
import User from '~/client/src/shared/models/User'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'

import FilterRow from './FilterRow'
import { ISignUpNotificationRulesApi } from './useSignUpNotificationRules'

interface IProps {
  rule: OperationRule
  ruleNumber: number
  canDelete: boolean
  store: ISignUpNotificationRulesApi
  companiesStore: CompaniesStore
  selectorUsers: User[]
  userMetadata: Map<string, IUserMetadata>
  currentUser?: ICurrentUserInfo
  filterData: IFilterData
  selectorCompanies: Company[]
  companyMetadata: Map<string, ICompanyMetadata>
}

const SignUpRuleRow: React.FC<IProps> = observer(
  ({
    rule,
    ruleNumber,
    canDelete,
    store,
    companiesStore,
    selectorUsers,
    userMetadata,
    currentUser,
    filterData,
    selectorCompanies,
    companyMetadata,
  }) => {
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)

    const filterState = store.getFilterState(rule)
    const users = store.getUsersFromAssignment(rule.assignmentsAsSubexpressions)

    const selectedUserIds = React.useMemo(
      () => new Set(users.map(u => u.id)),
      [users],
    )

    const handleOpenPopover = React.useCallback(() => {
      setIsPopoverOpen(true)
    }, [])

    const handleClosePopover = React.useCallback(() => {
      setIsPopoverOpen(false)
    }, [])

    const handleApply = React.useCallback(
      (newIds: Set<string>) => {
        store.applyUserChanges(rule, newIds)
        setIsPopoverOpen(false)
      },
      [rule],
    )

    const handleRemoveUser = React.useCallback(
      (userId: string) => {
        store.removeUserFromRule(rule, userId)
      },
      [rule],
    )

    return (
      <div className="tw:flex tw:flex-col tw:gap-2 tw:w-full">
        <span className="tw:text-[12px] tw:font-semibold tw:uppercase tw:tracking-[0.96px] tw:leading-4 tw:text-muted-foreground">
          {`${Localization.translator.rule} ${ruleNumber}`}
        </span>
        {/* Row 1: Label + User tags input */}
        <div className="tw:flex tw:items-start tw:gap-2 tw:w-full">
          <div className="tw:flex tw:gap-1 tw:items-center tw:flex-1 tw:pt-2">
            <span className="tw:text-title-sm tw:text-foreground">
              {Localization.translator.sendBicApprovalTo}
            </span>
            <TooltipWrapper
              className="tw:inline-flex tw:items-center"
              content={
                <div className="tw:max-w-[300px] tw:text-body-sm">
                  <p>{Localization.translator.bicApprovalTooltip}</p>
                </div>
              }
            >
              <InfoIcon size="xs" className="tw:block" />
            </TooltipWrapper>
          </div>
          <div className="tw:w-[400px] tw:shrink-0">
            <PopoverTagPicker
              isOpen={isPopoverOpen}
              onOpen={handleOpenPopover}
              onClose={handleClosePopover}
              placeholder={Localization.translator.allProjectCoordinators}
              triggerLabel={Localization.translator.sendBicApprovalTo}
              popoverClassName="tw:w-[400px] tw:max-h-[800px] tw:min-h-[600px]"
              popoverContent={
                <UserListSelector
                  selectedUserIds={selectedUserIds}
                  users={selectorUsers}
                  userMetadata={userMetadata}
                  currentUser={currentUser}
                  filterData={filterData}
                  title={Localization.translator.selectApprovers}
                  showActions
                  onClose={handleClosePopover}
                  onApply={handleApply}
                  className="tw:flex-1 tw:min-h-0"
                  defaultGrouping={GroupingOption.COMPANY}
                />
              }
            >
              {users.map(user => (
                <Tag
                  key={user.id}
                  avatar={
                    <UserProfilePreview
                      user={user}
                      isCompact
                      avatarSize={AvatarSize.XMinuscule}
                    />
                  }
                  onDismiss={() => handleRemoveUser(user.id)}
                >
                  {user.fullName}
                </Tag>
              ))}
            </PopoverTagPicker>
          </div>
          {canDelete ? (
            <button
              className="tw:flex tw:items-center tw:justify-center tw:w-7 tw:h-7 tw:rounded tw:border-none tw:bg-transparent tw:cursor-pointer tw:p-1 tw:shrink-0 tw:text-destructive"
              onClick={() => store.removeRule(rule.id)}
              title={Localization.translator.deleteRule}
            >
              <DeleteIcon size="sm" />
            </button>
          ) : (
            <div className="tw:w-7 tw:shrink-0" />
          )}
        </div>

        {/* Filter rows */}
        {filterState.filters.map((filter, index) => (
          <FilterRow
            key={`filter-${index}-${filter.category}-${filter.isNot}`}
            rule={rule}
            store={store}
            companiesStore={companiesStore}
            filterIndex={index}
            filter={filter}
            isFirstRow={index === 0}
            connector={
              index > 0 ? filterState.connectors[index - 1] : undefined
            }
            selectorCompanies={selectorCompanies}
            companyMetadata={companyMetadata}
          />
        ))}

        {/* Add Filter button */}
        <Button
          variant="ghost"
          className="tw:self-start tw:text-muted-foreground"
          onClick={() => store.addFilter(rule)}
        >
          <AddIcon data-icon="inline-start" size="xs" />
          {Localization.translator.addFilter}
        </Button>
      </div>
    )
  },
)

export default SignUpRuleRow
